import React, { useEffect, useState } from "react";
import { Share2, Video } from "lucide-react";
import { CTA, LINKS } from "../mock";
import { EASTERN_TIMEZONE, getBrowserTimezoneGroup, getClassSchedule } from "../lib/classSchedule";
import ShareModal from "./ShareModal";

const pad = (value) => String(value).padStart(2, "0");

function CountCell({ value, label }) {
  return (
    <div className="flex min-w-0 flex-col items-center" data-testid={`class-countdown-${label.toLowerCase()}`}>
      <div className="count-cell flex h-[48px] w-[44px] items-center justify-center rounded-lg text-center font-display text-lg tabular-nums min-[380px]:h-[50px] min-[380px]:w-[52px] min-[380px]:text-xl sm:h-[55px] sm:w-[60px] sm:text-[1.55rem]">{pad(value)}</div>
      <span className="mt-1 text-[9px] sm:text-[10px] font-mono-label" style={{ color: "rgba(32,24,80,0.5)" }}>{label}</span>
    </div>
  );
}

export default function ClassCard() {
  const [schedule, setSchedule] = useState(() => getClassSchedule());
  const [showShare, setShowShare] = useState(false);
  const [localZone] = useState(() => getBrowserTimezoneGroup());

  useEffect(() => {
    const id = window.setInterval(() => setSchedule(getClassSchedule()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const seconds = Math.floor(schedule.remainingMs / 1000);
  const values = [Math.floor(seconds / 86400), Math.floor((seconds % 86400) / 3600), Math.floor((seconds % 3600) / 60), seconds % 60];
  const dateLabel = schedule.classStart.toLocaleDateString("en-US", { timeZone: EASTERN_TIMEZONE, weekday: "long", month: "long", day: "numeric" });

  return (
    <>
      <div className="mx-auto max-w-5xl rounded-xl card-light p-5 sm:p-8" data-testid="class-card">
        {schedule.isLive && <div className="live-badge mx-auto mb-4 w-fit" data-testid="class-live-badge">LIVE</div>}
        <h3 className="text-center font-display glow-blue-text text-2xl leading-tight md:text-[1.85rem]" data-testid="class-card-headline" style={{ color: "#00A8E8" }}>
          {schedule.isLive ? "Class in Session: EazyBot Class — Join Now" : `Next Live Class — ${dateLabel}`}
        </h3>
        <div className="mt-6 flex items-start justify-center gap-1 min-[380px]:gap-1.5 sm:gap-2.5 md:gap-3" data-testid="class-card-countdown">
          <span className="hidden md:inline pt-3 italic text-sm" style={{ color: "#9A6E1C" }}>{schedule.isLive ? "Ends in" : "Starts in"}</span>
          {values.map((value, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="pt-2 text-lg sm:text-2xl" style={{ color: "#C69A3A" }}>:</span>}
              <CountCell value={value} label={["DAYS", "HRS", "MIN", "SEC"][index]} />
            </React.Fragment>
          ))}
        </div>
        <p className="mt-5 text-center font-mono-label text-[10px] sm:text-[11px]" style={{ color: "rgba(32,24,80,0.62)" }} data-testid="class-local-time-label">Your Local Time</p>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2" data-testid="class-timezone-pills">
          {CTA.timezones.map((zone) => {
            const active = zone.id === localZone;
            return <span key={zone.id} data-testid={`timezone-pill-${zone.id}`} data-active={active} className="rounded-full border px-3.5 py-1.5 text-xs font-semibold sm:text-sm" style={active ? { borderColor: "#C69A3A", background: "#C69A3A", color: "#fff" } : { borderColor: "rgba(32,24,80,0.18)", color: "rgba(32,24,80,0.8)" }}>{zone.label}</span>;
          })}
        </div>
      </div>
      <div className="mx-auto mt-5 flex max-w-5xl flex-col items-center justify-center gap-3 sm:flex-row" data-testid="class-actions">
        <a href={LINKS.liveClassZoom} target="_blank" rel="noopener noreferrer" data-testid="class-join-button" className={`btn-lift inline-flex min-h-[46px] min-w-[142px] items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold ${schedule.isLive ? "live-pulse" : ""}`} style={schedule.isLive ? { background: "#ef4444", color: "#fff" } : { background: "#201850", color: "#fff" }}>
          <Video className="h-4 w-4" aria-hidden="true" />
          {schedule.isLive ? "Join Live Class Now" : "Join Class"}
        </a>
        <button type="button" onClick={() => setShowShare(true)} data-testid="class-share-button" className="btn-lift inline-flex min-h-[46px] min-w-[113px] items-center justify-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold" style={{ borderColor: "rgba(198,154,58,0.65)", color: "#9A6E1C", background: "rgba(198,154,58,0.1)" }}>
          <Share2 className="h-4 w-4" aria-hidden="true" /> Share
        </button>
      </div>
      {showShare && <ShareModal onClose={() => setShowShare(false)} />}
    </>
  );
}